import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

interface ColorPickerProps {
  className?: string;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ className = '' }) => {
  const { colorScheme, setColorScheme, surface, setSurface } = useTheme();
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isLight = colorScheme === 'light';
  const isDark = colorScheme === 'dark' && surface !== 'black';
  const isBlack = surface === 'black';

  const selectLight = () => {
    setColorScheme('light');
    setSurface('default');
    setIsOpen(false);
  };

  const selectDark = () => {
    setColorScheme('dark');
    setSurface('default');
    setIsOpen(false);
  };

  const selectBlack = () => {
    setColorScheme('dark');
    setSurface('black');
    setIsOpen(false);
  };

  // Icône actuelle pour le bouton
  const CurrentIcon = isLight ? Sun : isDark ? Moon : Star;
  const currentColor = isLight ? 'text-yellow-500' : isDark ? 'text-gray-200' : 'text-yellow-400';

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        aria-label={t('navbar.toggleTheme')}
        title={t('navbar.toggleTheme')}
      >
        <CurrentIcon className={`h-4 w-4 ${currentColor}`} />
      </button>

      {/* Dropdown vertical avec les 3 options */}
      {isOpen && (
        <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="flex flex-col p-1 gap-1">
            {/* Light (Soleil) */}
            <button
              onClick={selectLight}
              className={`p-2 rounded-md transition-colors duration-200 ${
                isLight 
                  ? 'bg-yellow-100 dark:bg-yellow-900/30' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={t('colorPicker.light', 'Light')}
            >
              <Sun className={`h-4 w-4 ${isLight ? 'text-yellow-500' : 'text-gray-400'}`} />
            </button>

            {/* Dark (Lune) */}
            <button
              onClick={selectDark}
              className={`p-2 rounded-md transition-colors duration-200 ${
                isDark 
                  ? 'bg-blue-100 dark:bg-blue-900/30' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={t('colorPicker.dark', 'Dark')}
            >
              <Moon className={`h-4 w-4 ${isDark ? 'text-blue-400' : 'text-gray-400'}`} />
            </button>

            {/* Black (Étoile) */}
            <button
              onClick={selectBlack}
              className={`p-2 rounded-md transition-colors duration-200 ${
                isBlack 
                  ? 'bg-yellow-100 dark:bg-yellow-900/30' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={t('colorPicker.black', 'Black')}
            >
              <Star className={`h-4 w-4 ${isBlack ? 'text-yellow-400' : 'text-gray-400'}`} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};